import { Helmet } from "react-helmet-async";

const SITE = "https://fmsubastas.com";
const LOGO_URL = `${SITE}/logo-512.png`;
const DEFAULT_OG = "https://customer-assets.emergentagent.com/job_subasta-deuda-report/artifacts/x01sqrqt_Icono%202.0.png";

/**
 * SEO component for every page.
 *
 * Props:
 * - title:       page title (will be merged with brand)
 * - description: meta description (≤ 160 chars recommended)
 * - keywords:    optional comma-separated keywords (lower SEO weight today)
 * - path:        canonical path (e.g. "/consultoria"). Used to build canonical URL.
 * - image:       absolute URL for OG/Twitter card
 * - noindex:     true for legal/admin pages
 * - jsonLd:      array of structured-data objects (Schema.org). Will be injected as JSON-LD scripts.
 */
export default function SEO({
  title,
  description,
  keywords,
  path = "/",
  image = DEFAULT_OG,
  noindex = false,
  jsonLd = [],
}) {
  const fullTitle = title
    ? `${title} · Fernando Marcos Subastas & Investment`
    : "Fernando Marcos Subastas & Investment · Asesoría profesional en subastas BOE";
  const url = `${SITE}${path}`;
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {/* robots: SIEMPRE definido desde aquí (no en index.html estático) para
          que noindex realmente aplique en las páginas fallback (subasta no
          encontrada, admin, etc.) y evitemos "soft 404" en Google. */}
      <meta
        name="robots"
        content={noindex
          ? "noindex, nofollow"
          : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Fernando Marcos Subastas & Investment" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* Structured data */}
      {jsonLd.filter(Boolean).map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

// ---- Reusable Schema.org builders ----

export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE}/#organization`,
  name: "Fernando Marcos Subastas & Investment",
  alternateName: "FM Subastas & Investment",
  url: SITE,
  logo: {
    "@type": "ImageObject",
    url: LOGO_URL,
    width: 512,
    height: 512,
    caption: "FM Subastas & Investment",
  },
  image: LOGO_URL,
  description:
    "Asesoría profesional especializada en subastas públicas inmobiliarias del BOE en España. Informes de cargas y deudas, valoración registral y gestión post-subasta.",
  founder: {
    "@type": "Person",
    name: "Fernando Marcos Moya",
    jobTitle: "Asesor en subastas inmobiliarias",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Avenida Príncipe Salman, C1, 3.º B-W",
    addressLocality: "Marbella",
    addressRegion: "Málaga",
    postalCode: "29603",
    addressCountry: "ES",
  },
  areaServed: { "@type": "Country", name: "España" },
  email: "fmsubastasinvestment@gmail.com",
  telephone: "+34695536486",
  priceRange: "€€",
  vatID: "ES45091305N",
  knowsAbout: [
    "Subastas BOE",
    "Subastas judiciales",
    "Análisis de notas simples registrales",
    "Cargas registrales",
    "Hipotecas",
    "Embargos",
    "ITP",
    "Valor de referencia catastral",
    "Gestión post-subasta inmobiliaria",
  ],
  sameAs: [],
};

export function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

export function serviceJsonLd({ name, description, price, currency = "EUR", path }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    name,
    description,
    provider: {
      "@type": "ProfessionalService",
      name: "Fernando Marcos Subastas & Investment",
      url: SITE,
    },
    areaServed: { "@type": "Country", name: "España" },
    url: `${SITE}${path}`,
    offers: price
      ? {
          "@type": "Offer",
          price: String(price),
          priceCurrency: currency,
          availability: "https://schema.org/InStock",
          url: `${SITE}${path}`,
        }
      : undefined,
  };
}

export function faqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}
